// components/Breadcrumb
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

const Breadcrumb = ({
  items = [],
  loading = false,
  className = "",
  // Optional tabs for pages like SessionPage
  tabs = null,
  activeTab = null,
  onTabChange = null,
}) => {
  const navigate = useNavigate();
  const showTabs = tabs && tabs.length > 0 && onTabChange;
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to find the "Parent" link for the back button.
  // We look for the last item in the list that has an onClick handler
  // (excluding the current item if it happens to be clickable).
  const getBackAction = () => {
    // Reverse copy of items to find the "deepest" clickable ancestor
    const clickableItems = [...items].reverse().filter(item => item.onClick && !item.current);

    if (clickableItems.length > 0) {
      return clickableItems[0].onClick;
    }
    // Fallback default
    return () => navigate('/projects');
  };

  const renderItemContent = (item) => {
    const Icon = item.icon;
    const label = item.loading ? 'Loading...' : item.label;
    const iconClassName = item.emphasis || item.current
      ? 'text-red-600'
      : 'text-gray-400';

    return (
      <>
        {Icon && <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconClassName}`} />}
        <span className="truncate">{label}</span>
      </>
    );
  };

  return (
    <div className={`bg-white shadow-sm border-b border-gray-200 ${className} h-14`}>
      <div className="mx-auto px-4 h-full">
        <div className={`flex items-center ${showTabs ? 'justify-between gap-6' : 'space-x-2'} h-full`}>
          {/* Left side: Back button + Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm flex-shrink-0">
            {/* Back button */}
            <button
              onClick={getBackAction()}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              disabled={loading}
              title="Go Back"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>

            {items.map((item, index) => (
              <React.Fragment key={index}>
                {/* Breadcrumb Item */}
                <div className="flex items-center relative" ref={item.siblings ? dropdownRef : null}>
                  {/* Item with siblings dropdown */}
                  {item.siblings && item.siblings.length > 0 ? (
                    <>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                        disabled={loading}
                        className={`flex items-center gap-1.5 max-w-xs min-w-0 transition-colors ${
                          item.current
                            ? 'text-gray-900 font-medium hover:bg-gray-100 px-2 py-1 rounded-sm -mx-2'
                            : item.emphasis
                            ? 'text-gray-900 font-medium hover:text-gray-950'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        title={item.label}
                      >
                        {renderItemContent(item)}
                        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${openDropdown === index ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown */}
                      {openDropdown === index && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg z-50 min-w-[220px] max-h-[320px] overflow-y-auto">
                          <div className="py-1">
                            {item.siblings.map((sibling) => (
                              <button
                                key={sibling.id}
                                onClick={() => {
                                  setOpenDropdown(null);
                                  if (sibling.onClick) sibling.onClick();
                                }}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${
                                  sibling.current ? 'bg-gray-50' : ''
                                }`}
                              >
                                <span className="w-4 flex-shrink-0">
                                  {sibling.current
                                    ? <CheckIcon className="h-4 w-4 text-red-600" />
                                    : sibling.icon
                                      ? <sibling.icon className="h-4 w-4 text-gray-400" />
                                      : null}
                                </span>
                                <span className={`truncate ${sibling.current ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                  {sibling.label}
                                </span>
                              </button>
                            ))}
                          </div>
                          {item.backToParent && (
                            <>
                              <div className="border-t border-gray-100" />
                              <button
                                onClick={() => {
                                  setOpenDropdown(null);
                                  item.backToParent.onClick();
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <ArrowLeftIcon className="h-3.5 w-3.5" />
                                {item.backToParent.label}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  ) : item.onClick && !item.current ? (
                    <button
                      onClick={item.onClick}
                      disabled={loading}
                      className={`flex items-center gap-1.5 max-w-xs min-w-0 disabled:opacity-50 transition-colors hover:underline ${
                        item.emphasis
                          ? 'text-gray-900 font-medium hover:text-gray-950'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title={item.label}
                    >
                      {renderItemContent(item)}
                    </button>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1.5 max-w-xs min-w-0 ${
                        item.current
                          ? 'text-gray-900 font-medium'
                          : item.emphasis
                          ? 'text-gray-900 font-medium'
                          : 'text-gray-400'
                      }`}
                      title={item.label}
                    >
                      {renderItemContent(item)}
                    </span>
                  )}

                  {/* Action button (e.g., edit icon) */}
                  {item.action && (
                    <span className="ml-2 flex items-center">
                      {item.action}
                    </span>
                  )}
                </div>

                {/* Separator */}
                {index < items.length - 1 && (
                  <span className="text-gray-400 select-none">/</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {showTabs && (
            <nav className="flex h-full">
              <div className="flex items-center gap-6 h-full">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isDisabled = tab.disabled;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => !isDisabled && !loading && onTabChange(tab.id)}
                      disabled={isDisabled || loading}
                      className={`relative flex items-center px-1 h-full font-medium text-sm transition-all border-b-4 ${
                        isActive && !loading
                          ? 'border-red-600 text-red-600'
                          : isDisabled || loading
                          ? 'border-transparent text-gray-400 cursor-not-allowed'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                      }`}
                    >
                      {Icon && <Icon className="h-4 w-4 mr-1.5" />}
                      {tab.name}
                      {tab.badge && (
                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};

export default Breadcrumb;
